import { PauseIcon, PictureInPicture2Icon, PlayIcon, Volume2Icon, VolumeXIcon } from "lucide-react";
import { type RefObject, useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { formatClock } from "../lib/format";
import { cn } from "../lib/utils";
import { SeekBar } from "./seek-bar";

/**
 * Warstwa sterowania nad obrazem.
 *
 * Zastępuje natywne `controls` z dwóch powodów, z których żaden nie jest
 * estetyczny. Po pierwsze **natywny pasek nie umie pokazać podglądu klatki**,
 * więc arkusz miniatur byłby drugą martwą kolumną po `poster_path`. Po drugie
 * natywne sterowanie na telefonie ma cele wielkości kilku pikseli i nie da się
 * ich powiększyć — to element przeglądarki, nie strony.
 *
 * Czego świadomie NIE przenosimy: napisy i pełny ekran zostają tam, gdzie były,
 * czyli w prawym górnym narożniku. Napisy mają własną listę ścieżek i przesunięcie,
 * a przenoszenie ich w dół tylko po to, żeby wszystko było w jednym rzędzie,
 * przepisałoby działający kod bez zysku.
 *
 * Obraz w obrazie wrócił jako własny przycisk: to była realna utrata przy zdjęciu
 * natywnego sterowania, a nie teoretyczna — pozwala oglądać dalej po przejściu do
 * innej karty. AirPlay zostaje nieodzyskane, bo wymaga elementu `<video>`
 * z atrybutem `x-webkit-airplay` i sensownie działa tylko w Safari; dorobimy je,
 * gdy ktoś będzie z niego korzystał.
 */

/** Po tym czasie bezruchu sterowanie znika. Trzy sekundy to norma odtwarzaczy. */
const HIDE_AFTER_MS = 3000;

/**
 * Czy sterowanie ma być widoczne.
 *
 * Trzy wejścia, każde o czymś innym:
 *
 * - **`engaged`** — czy widz jest przy kadrze: kursor nad nim albo fokus w środku.
 *   To jest warunek nadrzędny. Pierwsza wersja go nie miała, więc pasek zostawał na
 *   obrazie także wtedy, gdy mysz stała w innej części ekranu — a przy zatrzymanym
 *   obrazie zostawał **na zawsze**, zasłaniając dolną część kadru.
 * - **`timerActive`** — czy od ostatniego ruchu nie minęły trzy sekundy. To zwykłe
 *   wygaszanie bezruchu, znane z każdego odtwarzacza.
 * - **`paused`** — zatrzymany obraz nie wygasza sterowania, bo pauza znaczy, że ktoś
 *   właśnie czegoś szuka. Ale nie **pokazuje** go wbrew `engaged`: pauza z klawiatury
 *   przy kursorze poza kadrem nie wyciąga paska na obraz.
 */
export function chromeVisible(input: {
  engaged: boolean;
  timerActive: boolean;
  paused: boolean;
}): boolean {
  return input.engaged && (input.timerActive || input.paused);
}

/**
 * Widoczność sterowania.
 *
 * Wydzielona, bo dotyczy DWÓCH grup przycisków: tej u dołu i tej w narożniku.
 * Gdyby każda liczyła to sama, jedna znikałaby chwilę po drugiej.
 *
 * Samą decyzję podejmuje `chromeVisible()` — tutaj zostaje wyłącznie zbieranie
 * zdarzeń, bo to one różnią się między myszą, dotykiem i pilotem.
 */
export function useChromeVisible(
  containerRef: RefObject<HTMLElement | null>,
  videoRef: RefObject<HTMLVideoElement | null>,
  /**
   * Czy kadr i obraz SĄ już w drzewie.
   *
   * Bez tego cały hak był martwy, i to od początku. Odtwarzacz montuje się ze stanem
   * `null` i pokazuje prostokąt zastępczy, więc przy pierwszym przebiegu efektów oba
   * `ref` są puste: nasłuch `play`/`pause` nie powstaje, a `paused` zostaje na stałe
   * `true`. Drugi efekt ma `paused` w zależnościach, więc też nie rusza ponownie —
   * czyli **żaden** nasłuch ruchu myszy nigdy się nie podpina i stoper wygaszania nie
   * zostaje uzbrojony ani razu.
   *
   * Objaw był mylący, bo wyglądał na decyzję projektową: sterowanie stało na obrazie
   * na stałe, zasłaniając napisy wtopione w materiał, a trzysekundowe wygaszanie
   * opisane w kodzie **nie zadziałało nigdy**. Ta sama pomyłka co przy dopasowaniu
   * kadru (`use-video-fit.ts`) — `ref` nie powiadamia o podłączeniu.
   */
  ready: boolean,
): boolean {
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(true);
  /**
   * Czy widz jest przy kadrze.
   *
   * Startowo `true`, żeby sterowanie było widoczne w pierwszej chwili po
   * uruchomieniu: kursor bywa już nad kadrem, a `pointerenter` nie wystrzeli, dopóki
   * mysz nie drgnie. Wygasza to potem zwykły stoper bezruchu.
   */
  const [engaged, setEngaged] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!ready) return;
    const video = videoRef.current;
    if (video === null) return;
    const sync = () => setPaused(video.paused);
    sync();
    video.addEventListener("play", sync);
    video.addEventListener("pause", sync);
    return () => {
      video.removeEventListener("play", sync);
      video.removeEventListener("pause", sync);
    };
  }, [videoRef, ready]);

  useEffect(() => {
    if (!ready) return;
    const container = containerRef.current;
    if (container === null) return;

    const arm = () => {
      setVisible(true);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      if (paused) return;
      timerRef.current = setTimeout(() => setVisible(false), HIDE_AFTER_MS);
    };

    /** Każde wejście w kadr — kursorem, palcem albo fokusem — jest obecnością. */
    const engage = () => {
      setEngaged(true);
      arm();
    };

    /**
     * Wyjście kursora kończy obecność natychmiast, bez stopera.
     *
     * Dotyk i pióro pomijamy: tam `pointerleave` przychodzi razem z puszczeniem palca,
     * więc traktowanie go jak wyjścia myszy schowałoby sterowanie w tej samej chwili,
     * w której widz je wywołał. Na dotyku obecność wygasza stoper bezruchu.
     */
    const leave = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      /**
       * Wciśnięty przycisk znaczy, że trwa przewijanie.
       *
       * Pasek przechwytuje wskaźnik właśnie po to, żeby ruch poza kadr nie urywał
       * przeciągania; schowanie sterowania w tej samej chwili odebrałoby widzowi
       * podgląd klatki, do którego celuje.
       */
      if (event.buttons !== 0) return;
      setEngaged(false);
    };

    /**
     * Fokus wychodzący z kadru kończy obecność — ale tylko, gdy naprawdę wyszedł.
     *
     * `relatedTarget` mówi, dokąd fokus poszedł: przejście między przyciskami wewnątrz
     * kadru też wywołuje `focusout`, a schowanie sterowania w środku poruszania się po
     * nim pilotem byłoby dokładnie tym, czego nawigacja przestrzenna ma nie robić.
     */
    const focusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (next instanceof Node && container.contains(next)) return;
      setEngaged(false);
    };

    arm();
    container.addEventListener("pointerenter", engage);
    container.addEventListener("pointermove", engage);
    // Dotknięcie ekranu ma sterowanie POKAZAĆ, a nie przełączyć: przełączanie
    // znaczyłoby, że pierwsze dotknięcie po ukryciu bywa kliknięciem w przycisk,
    // którego nie było widać.
    container.addEventListener("pointerdown", engage);
    container.addEventListener("pointerleave", leave);
    container.addEventListener("focusin", engage);
    container.addEventListener("focusout", focusOut);
    container.addEventListener("keydown", engage);

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      container.removeEventListener("pointerenter", engage);
      container.removeEventListener("pointermove", engage);
      container.removeEventListener("pointerdown", engage);
      container.removeEventListener("pointerleave", leave);
      container.removeEventListener("focusin", engage);
      container.removeEventListener("focusout", focusOut);
      container.removeEventListener("keydown", engage);
    };
  }, [containerRef, paused, ready]);

  return chromeVisible({ engaged, timerActive: visible, paused });
}

export function PlayerOsd({
  videoRef,
  durationSec,
  spriteVttUrl,
  canControl = true,
  visible,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  durationSec: number | null;
  spriteVttUrl: string | null;
  /** Follower w pokoju widzi postęp, ale o pozycji decyduje prowadzący. */
  canControl?: boolean;
  visible: boolean;
}) {
  const [paused, setPaused] = useState(true);
  const [current, setCurrent] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  /**
   * Obsługa obrazu w obrazie sprawdzana RAZ, nie przy każdym renderze.
   *
   * `pictureInPictureEnabled` bywa wyłączone politykami przeglądarki, a nie tylko
   * nieobsługiwane — więc pytamy o samą zgodę, nie o istnienie metody.
   */
  const [pipSupported] = useState(
    () => typeof document !== "undefined" && document.pictureInPictureEnabled,
  );

  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;

    const sync = () => {
      setPaused(video.paused);
      setMuted(video.muted);
      setVolume(video.volume);
    };
    const onTime = () => setCurrent(video.currentTime);

    sync();
    video.addEventListener("play", sync);
    video.addEventListener("pause", sync);
    video.addEventListener("volumechange", sync);
    video.addEventListener("timeupdate", onTime);
    return () => {
      video.removeEventListener("play", sync);
      video.removeEventListener("pause", sync);
      video.removeEventListener("volumechange", sync);
      video.removeEventListener("timeupdate", onTime);
    };
  }, [videoRef]);

  const total = durationSec ?? videoRef.current?.duration ?? null;

  return (
    <div
      data-slot="player-osd"
      className={cn(
        "absolute inset-x-0 bottom-0 z-10 flex flex-col gap-1 px-3 pb-2 transition-opacity duration-200 ease-tv",
        // Gradient, nie jednolite tło: obraz pod sterowaniem ma być widoczny,
        // a sam pasek czytelny nad każdą sceną, także nad białą.
        "bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-8",
        visible ? "opacity-100" : "pointer-events-none opacity-0",
      )}
    >
      <SeekBar
        videoRef={videoRef}
        durationSec={durationSec}
        spriteVttUrl={spriteVttUrl}
        disabled={!canControl}
      />

      <div className="flex items-center gap-2">
        {canControl && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              const video = videoRef.current;
              if (video === null) return;
              if (video.paused) void video.play().catch(() => undefined);
              else video.pause();
            }}
            aria-label={paused ? "Odtwórz" : "Wstrzymaj"}
            className="text-foreground hover:bg-foreground/15"
          >
            {paused ? <PlayIcon /> : <PauseIcon />}
          </Button>
        )}

        <span className="text-xs tabular-nums text-foreground/90">
          {formatClock(current)}
          {total !== null && ` / ${formatClock(total)}`}
        </span>

        {/*
          Ile ZOSTAŁO — osobno, bo to inna informacja niż pozycja.
          „Zdążę przed snem?" i „gdzie jestem w odcinku?" to dwa różne pytania, a
          odejmowanie w głowie z dwóch zegarów jest dokładnie tym, czego interfejs ma
          nie wymagać. Liczbę pokazujemy tylko wtedy, gdy znamy czas trwania i coś
          jeszcze zostało.
        */}
        {total !== null && total - current > 1 && (
          <span className="hidden text-xs tabular-nums text-foreground/60 sm:inline">
            −{formatClock(total - current)}
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          {/*
            Obraz w obrazie — tylko gdy przeglądarka go ma.
            Przycisk, który po kliknięciu nie robi nic, jest gorszy od jego braku:
            uczy, że interfejs kłamie.
          */}
          {pipSupported && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                const video = videoRef.current;
                if (video === null) return;
                if (document.pictureInPictureElement !== null) {
                  void document.exitPictureInPicture().catch(() => undefined);
                } else {
                  void video.requestPictureInPicture().catch(() => undefined);
                }
              }}
              aria-label="Obraz w obrazie"
              title="Obraz w obrazie — oglądaj dalej w małym okienku"
              className="text-foreground hover:bg-foreground/15"
            >
              <PictureInPicture2Icon />
            </Button>
          )}

          {/*
            Głośność tylko tam, gdzie jest kursor. Na telefonie i na telewizorze
            reguluje ją urządzenie, a suwak zabierałby miejsce na kontrolce, której
            nikt nie użyje.
          */}
          <div className="hidden items-center gap-1 sm:flex">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                const video = videoRef.current;
                if (video === null) return;
                video.muted = !video.muted;
              }}
              aria-label={muted ? "Włącz dźwięk" : "Wycisz"}
              className="text-foreground hover:bg-foreground/15"
            >
              {muted || volume === 0 ? <VolumeXIcon /> : <Volume2Icon />}
            </Button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={(e) => {
                const video = videoRef.current;
                if (video === null) return;
                const next = Number.parseFloat(e.target.value);
                video.volume = next;
                // Ruch suwaka w górę zdejmuje wyciszenie: inaczej suwak pokazuje
                // dźwięk, którego nie ma, i nie da się tego rozróżnić.
                if (next > 0 && video.muted) video.muted = false;
              }}
              aria-label="Głośność"
              className="h-1 w-20 accent-primary"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
